// useSessionSSE.ts — EventSource consumer for /api/v1/sessions/{id}/sse
//
// Per docs/frontend_eng_spec/sse_client.md:
//   - EventSource cannot set Authorization header → use ?token= query param
//     (5-min TTL; refreshable via session_get_detail when expired)
//   - 8 event types from owner_loop.md §4 (M0 emits 7; control_applied is
//     reserved for M1 control bus). Listener registers all 8 — extra is harmless.
//   - Exponential backoff (capped) on disconnect; counter resets on a healthy
//     onopen so transient blips over a long session don't trip the close gate.
//   - Last-Event-ID resume: appended to URL on reconnect (browser EventSource
//     would auto-set the header but a fresh EventSource() loses the counter).
//   - All scheduled timers tracked + cleared on unmount; no zombie reconnects.
import { useEffect, useRef, useState } from 'react';
import { useSessionStore } from '@/store/session';
import { sessionGetDetail } from '@/api/m0_sessions';
import { refreshStreamToken } from '@/api/stream_token';
import { getClerkToken } from '@/api/client';

export type SSEStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

interface UseSessionSSEOpts {
  sessionId: string;
  sseUrl: string;
  onComplete?: () => void;
}

/**
 * SSE_EVENT_TYPES — full event closed-set bound on EventSource.
 *
 * 9 既有 (M0/M0.5):
 *   phase_started · phase_summary · speech_chunk · iron_kill · v9_check
 *   · control_applied (M1 reserved · 保留以免 FE 静默忽略 BE 误发)
 *   · confirmation · error · close
 *
 * ★ M2 新增 (FE_SPEC §11.1 + §11.2 + P0-11):
 *   - phase_paused: GatingMode=user_advance · phase 末暂停 · 触发 AdvanceButton
 *   - phase_advanced: BE 在 advance 成功后写 transcript · 用于 replay
 *     还原 + force-reset awaiting_advance (P0-11)
 *
 * ★ M2.1 Wave 7 ARC-8 新增:
 *   - briefing_ready: POST /sessions 立即返 attachment_briefing.available=false
 *     占位 · 后台 Scribe goroutine 完成时 emit briefing_ready event 携最终结果
 *     (FE_SPEC §11.1 + sessions_m2.go:531 runAsyncScribe)
 *
 * ★ 严禁 PascalCase (PhaseStarted / PhasePaused) — 协议 = snake_case 闭集.
 */
export const SSE_EVENT_TYPES = [
  'phase_started',
  'phase_summary',
  'speech_chunk',
  'iron_kill',
  'v9_check',
  'control_applied',
  'confirmation',
  'error',
  'close',
  'phase_paused',
  'phase_advanced',
  'briefing_ready',
] as const;

export type SSEEventType = (typeof SSE_EVENT_TYPES)[number];

const MAX_ATTEMPTS = 5;
const BACKOFF_MS = [1_000, 2_000, 4_000, 5_000, 5_000];

export function useSessionSSE({ sessionId, sseUrl, onComplete }: UseSessionSSEOpts): {
  status: SSEStatus;
  reconnectCount: number;
} {
  const apply = useSessionStore((s) => s.applySSEEvent);
  const setError = useSessionStore((s) => s.setError);
  const [status, setStatus] = useState<SSEStatus>('idle');
  const [reconnectCount, setReconnectCount] = useState(0);
  const cancelledRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    cancelledRef.current = false;
    let es: EventSource | null = null;
    let retryHandle: ReturnType<typeof setTimeout> | null = null;
    let currentUrl = sseUrl;

    // FYD-2026-0512 review-mode: callers (Live.tsx review path) intentionally
    // pass sseUrl='' so the hook stays mounted (other effects gate on its
    // status) without ever opening an EventSource. `new EventSource('')`
    // would otherwise instantly fire onerror, exhaust retries, and surface
    // a bogus `sse_disconnect` error on the page.
    if (!sseUrl) {
      setStatus('idle');
      return () => {
        cancelledRef.current = true;
      };
    }

    const appendLastEventId = (url: string): string => {
      const lastId = useSessionStore.getState().current.last_event_id;
      if (!lastId) return url;
      const sep = url.includes('?') ? '&' : '?';
      // strip any prior last_event_id to avoid duplication on multi-reconnect
      const cleaned = url.replace(/[?&]last_event_id=\d+/g, '');
      const finalSep = cleaned.includes('?') ? '&' : '?';
      return `${cleaned}${cleaned === url ? sep : finalSep}last_event_id=${lastId}`;
    };

    const connect = (url: string, attempt: number) => {
      if (cancelledRef.current) return;
      setStatus(attempt === 0 ? 'connecting' : 'reconnecting');
      const finalUrl = attempt === 0 ? url : appendLastEventId(url);
      es = new EventSource(finalUrl, { withCredentials: false });

      es.onopen = () => {
        if (cancelledRef.current) return;
        setStatus('open');
        // Reset attempt counter — next disconnect starts fresh from 0.
        attempt = 0;
      };

      // Default 'message' channel unused — backend uses named events.
      es.onmessage = () => {};

      SSE_EVENT_TYPES.forEach((t) => {
        es!.addEventListener(t, (raw: Event) => {
          // ★ Phase 4d FE 联调测试 surfaced bug: EventSource fires a native
          // 'error' Event (NOT MessageEvent) on connection failure, which
          // landed in the SSE_EVENT_TYPES.error addEventListener and got
          // forwarded to the store reducer with `data={}` → reducer's case
          // 'error' set cur.error.code='malformed_sse_event' → Live.tsx
          // unconditionally rendered the ErrorState, masking all UI. Filter
          // here: BE-emitted SSE events are MessageEvent with data; raw
          // browser Events without `data` are connection-state, NOT
          // application errors.
          if (!(raw instanceof MessageEvent)) return;
          try {
            // ★ pass-1 fix C1: intersection type instead of `as any`.
            // MessageEvent.lastEventId is a SSE-only property; widen the type
            // here without losing strictness elsewhere.
            const e = raw as MessageEvent & { lastEventId: string };
            const data = e.data ? JSON.parse(e.data) : {};
            const id = parseInt(e.lastEventId || '0', 10);
            apply({ type: t, id, data });
            // ★ FYD-2026-0515 sse_disconnect bug: BE emits `event: close` at
            // owner_finished / already_completed / already_aborted (the last
            // two from writeTerminalCloseStream in sse_handler.go on reconnect
            // to a GC'd sink). Without this branch the connection drop that
            // follows triggers onerror → 5x retry → bogus sse_disconnect.
            // Cancel the EventSource, drop any pending retry, and forward the
            // completion signal to the parent so it can navigate to /report.
            if (t === 'close') {
              cancelledRef.current = true;
              if (retryHandle) {
                clearTimeout(retryHandle);
                retryHandle = null;
              }
              es?.close();
              setStatus('closed');
              const reason = (data as { reason?: string }).reason;
              if (reason === 'owner_finished' || reason === 'already_completed') {
                onCompleteRef.current?.();
              }
              return;
            }
            if (t === 'confirmation' && data.action === 'report_ready') {
              onCompleteRef.current?.();
            }
          } catch (err) {
            console.warn('sse parse error', t, err);
          }
        });
      });

      es.onerror = async () => {
        if (cancelledRef.current) return;
        es?.close();
        if (attempt >= MAX_ATTEMPTS) {
          setStatus('closed');
          setError({
            code: 'sse_disconnect',
            message: {
              i18n_key: 'error.sse.disconnect',
              default_text_zh: '连接已断开',
              default_text_en: 'SSE disconnect',
            },
            trace_id: '',
          });
          return;
        }
        setReconnectCount((n) => n + 1);
        // ★ M2.1 Wave 7 ARC-9: stream token refresh on connection close.
        // EventSource cannot read response status / body, but BE rejects
        // expired tokens with 401 stream_token_expired which surfaces here
        // as a generic onerror. Optimistic refresh: extract current ?token=
        // from URL, POST /stream-token/refresh once. On success use new
        // sse_url. On failure fall through to sessionGetDetail (legacy path)
        // which can also mint a fresh sse_url. Refresh attempts are capped
        // (≤2 across the reconnect cycle) by the outer attempt counter so we
        // do not spin if both refresh + getDetail keep failing.
        let refreshed = false;
        try {
          const oldToken = (currentUrl.match(/[?&]token=([^&]+)/) || [])[1];
          const clerkJwt = await getClerkToken();
          if (oldToken && clerkJwt) {
            const out = await refreshStreamToken(sessionId, oldToken, clerkJwt);
            if (out?.sse_url) {
              currentUrl = out.sse_url;
              refreshed = true;
            }
          }
        } catch {
          // refresh fail → fall back to getDetail
        }
        if (!refreshed) {
          try {
            const detail = (await sessionGetDetail(sessionId)) as {
              sse_url?: string;
              status?: string;
            };
            // ★ FYD-2026-0515: if the session already reached a terminal
            // state, the BE has no live sse_url to mint. Continuing the retry
            // loop just exhausts MAX_ATTEMPTS and surfaces a fake disconnect.
            // Drop out cleanly and let onComplete navigate the user to the
            // report (terminal='aborted' just stops; Live.tsx already renders
            // the abort banner from session_get_detail in the recovery path).
            if (detail?.status === 'completed' || detail?.status === 'aborted') {
              cancelledRef.current = true;
              setStatus('closed');
              if (detail.status === 'completed') {
                onCompleteRef.current?.();
              }
              return;
            }
            if (detail?.sse_url) currentUrl = detail.sse_url;
          } catch {
            // leave currentUrl as-is
          }
        }
        const delay = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
        retryHandle = setTimeout(() => connect(currentUrl, attempt + 1), delay);
      };
    };

    connect(currentUrl, 0);

    return () => {
      cancelledRef.current = true;
      if (retryHandle) clearTimeout(retryHandle);
      es?.close();
      setStatus('closed');
    };
  }, [sessionId, sseUrl, apply, setError]);

  return { status, reconnectCount };
}
